import { createContext, useContext, useEffect, useSyncExternalStore } from "react";

/**
 * The shared timeline cursor, split OUT of the monolithic `TimelineContext`
 * value into a per-provider-instance external store.
 *
 * Why: during live playback the primary video's RAF loop writes the cursor
 * ~30×/sec. When the cursor lived on the one memoized context value, every
 * consumer under the provider re-rendered on every frame (all video tiles, the
 * stack shell, the active row). Moving the cursor here lets consumers pick their
 * cost explicitly:
 *   - effect-only readers (seek detection, live-edge watcher, cursor reporter,
 *     video-data index) subscribe WITHOUT re-rendering,
 *   - the playhead uses {@link useSelectedTime} and re-renders only itself,
 *   - non-cursor consumers of the monolithic value fall silent on ticks.
 *
 * `setSelectedTime` stays mirrored on the monolithic value (stable identity), so
 * writer-only consumers need no change.
 */

export type CursorOrigin = "internal" | "external" | "video";

export interface CursorSnapshot {
  selectedTime: number | null;
  lastOrigin: CursorOrigin;
}

export interface CursorStore {
  /** Stable snapshot ref while unchanged; a NEW object only on an actual change. */
  getSnapshot: () => CursorSnapshot;
  subscribe: (listener: () => void) => () => void;
  setSelectedTime: (time: number | null, origin?: CursorOrigin) => void;
}

/**
 * Vanilla store holding the cursor. One instance per timeline provider instance
 * (lazy-created in `useTimelineProviderValue`).
 */
export const createCursorStore = (): CursorStore => {
  let snapshot: CursorSnapshot = { selectedTime: null, lastOrigin: "internal" };
  const listeners = new Set<() => void>();

  const getSnapshot = (): CursorSnapshot => snapshot;

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const setSelectedTime = (time: number | null, origin: CursorOrigin = "internal"): void => {
    // Mirror React's setState dedupe: a write that changes neither the time nor
    // the origin is a no-op and MUST NOT notify. The video RAF loop relies on
    // this exactly matching the old useState bail-out — it arms
    // `isPlaybackWriteRef` only for changed values, and the seek detector
    // consumes that arm on the resulting notify, so arm→consume stays 1:1.
    if (snapshot.selectedTime === time && snapshot.lastOrigin === origin) return;
    // New object so `getSnapshot` identity changes exactly on real changes.
    snapshot = { selectedTime: time, lastOrigin: origin };
    // Notify SYNCHRONOUSLY, in subscription order, so effect-only readers observe
    // every change in the same order the old cursor effects did. Callbacks only
    // touch refs / seek the media element / call page callbacks / schedule React
    // state — none writes the cursor synchronously, so this can't recurse.
    for (const listener of listeners) listener();
  };

  return { getSnapshot, subscribe, setSelectedTime };
};

/** Stable per-instance store handle. Provided alongside the monolithic value. */
export const CursorStoreContext = createContext<CursorStore | undefined>(undefined);

export const useCursorStore = (): CursorStore => {
  const store = useContext(CursorStoreContext);
  if (!store) {
    throw new Error("useCursorStore must be used within a TimelineProvider");
  }
  return store;
};

/**
 * Render-reactive cursor time. PRIMITIVE snapshot, so no memo is needed and
 * `useSyncExternalStore`'s Object.is check is exact. Re-renders the caller only
 * when the time actually changes — use it for the playhead and machine-detail
 * readers, never for effect-only work.
 */
export const useSelectedTime = (): number | null => {
  const store = useCursorStore();
  return useSyncExternalStore(store.subscribe, () => store.getSnapshot().selectedTime);
};

/**
 * Render-reactive cursor origin. Changes rarely (only on origin transitions), so
 * it does not re-render on same-origin playback ticks.
 */
export const useLastOrigin = (): CursorOrigin => {
  const store = useCursorStore();
  return useSyncExternalStore(store.subscribe, () => store.getSnapshot().lastOrigin);
};

/**
 * Subscribe to cursor changes WITHOUT re-rendering. `cb` fires synchronously on
 * every change with the fresh snapshot (not on mount — subscribe-only). The
 * caller MUST memoize `cb`; it is a dependency of the subscription effect.
 */
export const useCursorEffect = (cb: (snap: CursorSnapshot) => void): void => {
  const store = useCursorStore();
  useEffect(() => {
    return store.subscribe(() => cb(store.getSnapshot()));
  }, [store, cb]);
};
